/**
 * src/lib/voice/useAudioSessionCleanup.js — Phase 76-02 Task 3
 *
 * Mounts an audio session monitor while the voice session is "live"
 * (state !== 'idle' && state !== 'error'). Watches visibility, device
 * changes, AudioContext interrupts, and stream loss — escalates to
 * cleanup or error callbacks on the parent.
 *
 * Extracted from useVoiceConversation so the orchestrator hits the
 * ≤300-line rule (CLAUDE.md code size guidelines).
 */

import { useEffect, useRef } from 'react';
import { createAudioSessionMonitor } from './audioSession';
import { logVoiceEvent } from './voiceLogger';

export default function useAudioSessionCleanup({
  state,
  audioContextRef,
  streamRef,
  onCleanup,
  onStreamLost,
} = {}) {
  const monitorRef = useRef(null);

  useEffect(() => {
    if (state === 'idle' || state === 'error') {
      if (monitorRef.current) {
        try { monitorRef.current.stop(); } catch { /* noop */ }
        monitorRef.current = null;
      }
      return undefined;
    }

    const monitor = createAudioSessionMonitor({
      onHidden: () => {
        logVoiceEvent('audio_session_hidden', 'ok', 'document.hidden=true — cleanup');
        onCleanup?.('hidden');
      },
      onInterrupt: (s) => {
        logVoiceEvent('audio_session_interrupted', 'ok', `AudioContext=${s}`, { audio_state: s });
        onCleanup?.('interrupted');
      },
      onStreamLost: () => {
        logVoiceEvent('audio_stream_lost', 'error', 'MediaStream track ended unexpectedly');
        onStreamLost?.();
      },
      onRouteChange: () => {
        logVoiceEvent('audio_route_change', 'ok', 'mediaDevices devicechange — OS is routing');
      },
      deps: {
        audioContext: audioContextRef?.current || null,
        stream: streamRef?.current || null,
      },
    });
    monitorRef.current = monitor;

    return () => {
      try { monitor.stop(); } catch { /* noop */ }
      if (monitorRef.current === monitor) monitorRef.current = null;
    };
  }, [state, audioContextRef, streamRef, onCleanup, onStreamLost]);
}
